import React from "react";
import { openModal } from "reducers/Modal";
import {
	hasPermission,
	isDynamic,
	isIncomplete,
	isSocialCreative,
	transcodingChecks,
	isSocialPreviewDisabled,
	noop
} from 'utils/common';
import { BasicAction, LinkAction } from "widgets/ActionMenu";
import {
	TIA_DOMAIN,
	PREVIEW_2_URL,
	MODAL_TYPES,
	CV,
	CREATIVE_VIEW
} from "utils/constants";

import { getPRVW } from "utils/Flags";
import { isLegacyPreview } from "utils/Preview";

import{ isInvalidDVASTState, shouldDisplayAction } from 'utils/CreativeActions';
import { useDispatch } from "react-redux";

export function getLegacyPreviewVersionUrl(creativeId, name){
	return `${TIA_DOMAIN}/tia/${creativeId}?configurationName=${name}`;
}

export function legacyPreviewVersion(creativeId, name){
	window.open(getLegacyPreviewVersionUrl(creativeId, name), "_BLANK");
}

export function getPreviewVersionUrl(creativeId, versionName){
	return `${PREVIEW_2_URL}/creative/${creativeId}?panel=versions&selectedVersion=${versionName}`;
}

export function previewVersion(creativeId, versionName){
	window.open(getPreviewVersionUrl(creativeId, versionName), "_BLANK");
}

export function previewVersionsPrivate(id, name, type = "private") {
	window.open(`${TIA_DOMAIN}/preview/${id}`);
}

export function previewCreativePrivate(id, type = "private"){
	window.open(`${TIA_DOMAIN}/tia/${id}`, "_BLANK");
}

export function previewCreative(ids){
	window.open(`${PREVIEW_2_URL}/creative/${ids.join(',')}`, "_BLANK");
}

export function createMockup(id){
	window.open(`${TIA_DOMAIN}/mockup/${id}`, "_BLANK");
}

export function BasicPreviewAction({ icon, name, pathFn = noop }, rowInfo){
	let path = pathFn(rowInfo)
		, className = "external-link-action preview-actions"
		, handleClick = () => window.open(path, "_BLANK");

	return (
		<BasicAction
			className={className}
			iconName={icon}
			name={name}
			handleClick={handleClick}
		/>
	);
}

export const QUICK_PREVIEW = {
	icon:"far fa-eye",
	name:"Quick Preview",
	bulkAction: false,
	component: ({ dependentValues }) => {
		let { libraryId, creativeId } = dependentValues
			, previewType = isSocialCreative(dependentValues) ? "social" : "basic";

		return(<LinkAction
			key="quick-creative-preview"
			name="Quick Preview"
			iconName="far fa-eye"
			useMenuItem
			className="action-link action-item"
			path={`/library/${libraryId}/modal/preview-creative/${previewType}/${creativeId}/`} />);
	},
};

export const PREVIEW_CREATIVE_PRIVATE = {
	icon:"fas fa-image",
	name:"Preview Creative",
	bulkAction:true,
	pathFn:(dependentValues) => {
		if(getPRVW()){
			return `${PREVIEW_2_URL}/creative/${dependentValues.creativeId}`;
		}

		return `${TIA_DOMAIN}/tia/${dependentValues.creativeId}`;
	},
	shouldDisplay:({ dependentValues }) => shouldDisplayAction.privateCreativePreview(dependentValues),
	component: ({ dependentValues }) => BasicPreviewAction({ ...PREVIEW_CREATIVE_PRIVATE }, dependentValues),
};

const PREVIEW_CREATIVE_PUBLIC = {
	icon:"fas fa-image",
	name:"Preview Creative (Public)",
	bulkAction:true,
	shouldDisplay:()=>{return true;},
	component: ({ dependentValues }) => BasicPreviewAction("fas fa-image", "Preview Creative (Public)", dependentValues),
};

export const PREVIEW_VERSION_PRIVATE = {
	icon: "fas fa-bolt",
	name: "Preview Versions",
	bulkAction: true,
	pathFn: (dependentValues) => {
		const { creativeId } = dependentValues || {};

		let url = `${PREVIEW_2_URL}/creative-versions/${creativeId}`;

		if(isLegacyPreview(dependentValues)){
			url = `${TIA_DOMAIN}/preview/${creativeId}`;
		}

		return url;
	},
	shouldDisplay: ({ dependentValues }) => isDynamic(dependentValues) && !isInvalidDVASTState(dependentValues),
	component: ({ dependentValues }) => BasicPreviewAction({ ...PREVIEW_VERSION_PRIVATE }, dependentValues),
};

const PREVIEW_VERSION_PUBLIC = {
	icon:"fas fa-bolt",
	name:"Preview Versions (Public)",
	bulkAction:true,
	shouldDisplay:({ dependentValues }) => isDynamic(dependentValues),
	component: ({ dependentValues }) => BasicPreviewAction("fas fa-bolt", "Preview Versions (Public)", dependentValues),
};

export const CREATE_MOCKUP = {
	icon:"fas fa-window",
	name:"Create Mockup",
	bulkAction:true,
	pathFn:dependentValues=>`${TIA_DOMAIN}/mockup/${dependentValues.libraryId}`,
	shouldDisplay:({ user })=>{
		return hasPermission(user, 'Creative View', 'Create Mock-up Display');
	},
	component:({ dependentValues }) => BasicPreviewAction({ ...CREATE_MOCKUP }, dependentValues),
};

//=============SOCIAL_PREVIEW_ACTIONS=============//

export const PREVIEW_CREATIVE = {
	icon:"fas fa-image",
	name:"Preview Creative",
	pathFn:dependentValues => `${PREVIEW_2_URL}/creative/${dependentValues.creativeId}`,
	component: ({ dependentValues }) => {
		let action = PREVIEW_CREATIVE;

		if (isLegacyPreview(dependentValues)) {
			action = PREVIEW_CREATIVE_PRIVATE;
		}

		return BasicPreviewAction({ ...action }, dependentValues);
	},
};

export const SHARE_CREATIVE = {
	icon:"fas fa-share-square",
	iconName:"fas fa-share-square",
	name:"Share Creative",
	shouldDisplay: ({ user, dependentValues }) => {
		return hasPermission(user, CV, CREATIVE_VIEW.SHARE_PREVIEW)
			&& !isIncomplete(dependentValues)
			&& !transcodingChecks.transcoding(dependentValues)
			&& !isSocialPreviewDisabled(dependentValues)
		;
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const { creativeId } = dependentValues || {};
		return (
			<BasicAction
				{...SHARE_CREATIVE}
				{...dependentValues}
				handleClick = {() => {
					dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, { creativeIds: [creativeId]}));
				}}
			/>
		);
	},
};

export const PREVIEW_VERSIONS = {
	icon: "fas fa-bolt",
	name: "Preview Versions",
	pathFn: dependentValues => `${PREVIEW_2_URL}/creative-versions/${dependentValues.creativeId}`,
	shouldDisplay: ({ dependentValues })=>{
		let { numberOfVersions = 0 } = dependentValues || {};
		return (numberOfVersions > 1);
	},
	component: ({ dependentValues }) => BasicPreviewAction({ ...PREVIEW_VERSIONS  }, dependentValues),
};

export const SHARE_VERSIONS = {
	icon: "fas fa-share-square",
	iconName: "fas fa-share-square",
	name:"Share Versions",
	shouldDisplay: ({ dependentValues, user }) => {
		return SHARE_CREATIVE.shouldDisplay({ dependentValues, user }) && isDynamic(dependentValues);
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const { creativeId } = dependentValues || {};
		return (
			<BasicAction
				{...SHARE_VERSIONS}
				{...dependentValues}
				handleClick = {() => {
					dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, {
						creativeIds: [creativeId],
						useVersionLinks: true,
					}));
				}}
			/>
		);
	},
};

export const PREVIEW_ACTION_LIST = [
	[QUICK_PREVIEW],
	[PREVIEW_CREATIVE, SHARE_CREATIVE],
	[PREVIEW_VERSION_PRIVATE, SHARE_VERSIONS],
	[CREATE_MOCKUP]
];

export const SOCIAL_PREVIEW_ACTION_LIST = [
	[QUICK_PREVIEW],
	[PREVIEW_CREATIVE, SHARE_CREATIVE],
	[PREVIEW_VERSIONS, SHARE_VERSIONS]
];

export const PRIVATE ={
	QUICK_PREVIEW,
	PREVIEW_CREATIVE_PRIVATE,
	PREVIEW_CREATIVE_PUBLIC,
	PREVIEW_VERSION_PRIVATE,
	PREVIEW_VERSION_PUBLIC,
	CREATE_MOCKUP,
};
